import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Check, X, FileSpreadsheet, Clock } from 'lucide-react';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const AttendanceSheet = () => {
    // Default to the current month in "YYYY-MM" format
    const [monthYear, setMonthYear] = useState(new Date().toISOString().slice(0, 7));
    const axiosSecure = UseAxiosSecure();
    const { activeBranchId } = UseAuth();

    // FETCH THE SHEET DATA
    const { data: queryData, isLoading } = useQuery({
        queryKey: ['attendance-sheet', activeBranchId, monthYear],
        queryFn: async () => {
            const res = await axiosSecure.get('/hr/attendance-sheet', {
                params: { monthYear }
            });
            return res.data?.data || { sheet: [], daysInMonth: 30 };
        },
        enabled: !!activeBranchId
    });

    const sheet = queryData?.sheet || [];
    const daysInMonth = queryData?.daysInMonth || 30;

    // Helper to generate an array of days [1, 2, 3... 31]
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Attendance Data...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* --- HEADER & MONTH PICKER --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <FileSpreadsheet className="text-cyan-500" /> Master Attendance
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Review daily presence, leaves, and absences.</p>
                </div>
                
                <div className="relative group">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-500 transition-colors" size={18} />
                    <input 
                        type="month" 
                        value={monthYear}
                        onChange={(e) => setMonthYear(e.target.value)}
                        className="bg-[#0f0a1f] border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-all cursor-pointer"
                    />
                </div>
            </div>

            {/* --- LEGEND --- */}
            <div className="flex flex-wrap gap-4 px-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-emerald-500/20 border border-emerald-500"></div> Present</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500"></div> Absent (Blank)</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-orange-500/20 border border-orange-500"></div> On Leave</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-cyan-500/20 border border-cyan-500"></div> Pending Leave</span>
            </div>

            {/* --- EXCEL-STYLE MATRIX --- */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-[#1a103c]/80 z-20 flex items-center justify-center">
                        <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left border-collapse min-w-[1000px]">
                        <thead>
                            <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-400 bg-white/5">
                                <th className="p-4 font-semibold sticky left-0 bg-[#1a103c] z-10 border-r border-white/10 w-48">Employee</th>
                                {daysArray.map(day => (
                                    <th key={day} className="p-2 text-center min-w-[40px] border-r border-white/5 last:border-none">
                                        {day}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {sheet.length === 0 && !isLoading && (
                                <tr>
                                    <td colSpan={daysInMonth + 1} className="p-10 text-center text-slate-500">
                                        No staff found. Add employees to the roster first.
                                    </td>
                                </tr>
                            )}
                            {sheet.map((emp) => (
                                <tr key={emp._id} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-none text-xs">
                                    
                                    {/* EMPLOYEE NAME COLUMN (Sticky on scroll) */}
                                    <td className="p-3 sticky left-0 bg-[#1a103c] group-hover:bg-[#23174d] transition-colors border-r border-white/10 z-10 w-48">
                                        <div className="flex items-center gap-2">
                                            <img 
                                                src={emp.image || `https://ui-avatars.com/api/?name=${emp.name}&background=random`} 
                                                alt="Profile" 
                                                className="w-8 h-8 rounded-lg object-cover border border-white/10"
                                            />
                                            <h3 className="font-bold text-white truncate">{emp.name}</h3>
                                        </div>
                                    </td>

                                    {/* DAY COLUMNS */}
                                    {daysArray.map(day => {
                                        const status = emp.records[day];
                                        
                                        // Determine Color and Icon based on Status
                                        let bgClass = "bg-white/5 hover:bg-white/10";
                                        let content = <span className="text-slate-600">-</span>; // Default = Absent / No Record

                                        if (status === 'Present') {
                                            bgClass = "bg-emerald-500/10 border-emerald-500/30 text-emerald-400";
                                            content = <Check size={14} className="mx-auto" />;
                                        } else if (status === 'On Leave' || status === 'Approved') {
                                            bgClass = "bg-orange-500/10 border-orange-500/30 text-orange-400";
                                            content = <span className="font-black">L</span>;
                                        } else if (status === 'Pending') {
                                            bgClass = "bg-cyan-500/10 border-cyan-500/30 text-cyan-400";
                                            content = <Clock size={14} className="mx-auto" />;
                                        } else if (status === 'Absent' || status === 'Rejected') {
                                            bgClass = "bg-red-500/10 border-red-500/30 text-red-500";
                                            content = <X size={14} className="mx-auto" />;
                                        }

                                        return (
                                            <td key={day} className={`p-1 border-r border-white/5 border-y-0 text-center`}>
                                                <div className={`w-8 h-8 mx-auto rounded flex items-center justify-center border border-transparent transition-colors cursor-default ${bgClass}`} title={`${emp.name} - Day ${day}: ${status || 'No Data'}`}>
                                                    {content}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AttendanceSheet;